import { useState, useEffect, useCallback } from "react";
import {
  Globe,
  Building2,
  MapPin,
  Tag,
  ShoppingCart,
  DollarSign,
  Mail,
  Phone,
  X,
  ExternalLink,
  CheckCircle2,
  Shield,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Check,
  Minus,
  Clock,
  PhoneCall,
  MessageSquare,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
} from "lucide-react";
import { toast } from "react-hot-toast";
import type { ColumnDef } from "@tanstack/react-table";
import type { Lead, ContactLog } from "../../services/admin/leads.admin.service";
import { adminLeadsService } from "../../services/admin/leads.admin.service";
import { DataTable } from "../ui/data-table";

interface LeadsTableProps {
  leads: Lead[];
  loading: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
  allSelected?: boolean;
  /** Rendered inside the table card, below the table (e.g. a pagination footer). */
  footer?: React.ReactNode;
}

function ContactCell({ lead }: { lead: Lead }) {
  const emails = Array.isArray(lead.emails) ? lead.emails : [];
  const hasEnrichedContacts = lead.enrichedAt && emails.length > 0;

  if (hasEnrichedContacts) {
    const firstEmail = emails[0];
    const remaining = emails.length - 1;
    return (
      <div className="flex flex-col gap-0.5 max-w-[200px]">
        <span className="text-xs text-muted-foreground truncate" title={firstEmail.email}>
          {firstEmail.email}
        </span>
        <div className="flex items-center gap-1.5">
          {lead.phone && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Phone size={9} />
              {lead.phone}
            </span>
          )}
          {remaining > 0 && (
            <span className="text-[10px] text-[#d7785b] font-medium">
              +{remaining} more
            </span>
          )}
        </div>
      </div>
    );
  }

  const hasEmailCount = lead.emailCount != null && lead.emailCount > 0;
  const hasPhoneCount = lead.phoneCount != null && lead.phoneCount > 0;

  if (hasEmailCount || hasPhoneCount) {
    return (
      <div className="flex items-center gap-1.5">
        {hasEmailCount && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-full">
            <Mail size={10} />
            {lead.emailCount}
          </span>
        )}
        {hasPhoneCount && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-full">
            <Phone size={10} />
            {lead.phoneCount}
          </span>
        )}
      </div>
    );
  }

  if (lead.contactAvailable) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 rounded-full">
        <Mail size={10} />
        Available
      </span>
    );
  }

  return <span className="text-xs text-muted-foreground">No</span>;
}

function DeliverabilityBadge({ score }: { score?: string }) {
  if (!score) return null;
  const colors: Record<string, string> = {
    deliverable: "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10",
    risky: "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10",
    undeliverable: "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10",
  };
  const cls = colors[score.toLowerCase()] || "text-muted-foreground bg-muted";
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${cls}`}>
      {score}
    </span>
  );
}

function ValidationStatusBadge({
  status,
}: {
  status?: string | null;
}) {
  if (!status) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted">
        <Minus size={14} className="text-muted-foreground" />
      </span>
    );
  }
  if (status === "pending" || status === "in_progress") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-full"
        title="Email validation in progress"
      >
        <Loader2 size={9} className="animate-spin" />
        {status === "pending" ? "Pending" : "Running"}
      </span>
    );
  }
  if (status === "completed") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-full"
        title="Email validation complete"
      >
        <ShieldCheck size={9} />
        Done
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-full"
        title="Email validation failed"
      >
        <ShieldX size={9} />
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-muted rounded-full">
      <ShieldAlert size={9} />
      {status}
    </span>
  );
}

function isUndeliverableEntry(entry: { email_deliverability?: string }): boolean {
  return (
    (entry.email_deliverability || "").toLowerCase() === "undeliverable"
  );
}

function LeadDetailModal({
  lead,
  onClose,
  onLeadUpdated,
}: {
  lead: Lead;
  onClose: () => void;
  onLeadUpdated?: (updated: Partial<Lead>) => void;
}) {
  const emails = Array.isArray(lead.emails) ? lead.emails : [];
  const selectableEmails = emails.filter((e: any) => !isUndeliverableEntry(e));

  const canSendEmail =
    !!lead.enrichedAt && selectableEmails.length > 0;

  const [checkedEmails, setCheckedEmails] = useState<Set<string>>(
    () => new Set(selectableEmails.map((e: any) => e.email))
  );
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    sent: number;
    failed: number;
  } | null>(null);

  const [contactLogs, setContactLogs] = useState<ContactLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [phoneNote, setPhoneNote] = useState("");
  const [loggingPhone, setLoggingPhone] = useState(false);

  const fetchContactLogs = useCallback(async () => {
    try {
      const logs = await adminLeadsService.getContactLogs(lead.id);
      setContactLogs(logs);
    } catch {
      // silently fail
    } finally {
      setLogsLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    fetchContactLogs();
  }, [fetchContactLogs]);

  const handleLogPhoneContact = async () => {
    setLoggingPhone(true);
    try {
      await adminLeadsService.createContactLog(lead.id, "phone", phoneNote || undefined);
      toast.success("Phone contact logged");
      setPhoneNote("");
      setShowPhoneForm(false);
      fetchContactLogs();
      onLeadUpdated?.({ phoneContactedAt: new Date().toISOString() });
    } catch (error: any) {
      toast.error(error.message || "Failed to log contact");
    } finally {
      setLoggingPhone(false);
    }
  };

  const toggleEmail = (email: string, blocked: boolean) => {
    if (blocked) return;
    setCheckedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectableEmails.length === 0) {
      setCheckedEmails(new Set());
      return;
    }
    const allSelected = selectableEmails.every((e: any) =>
      checkedEmails.has(e.email)
    );
    if (allSelected) {
      setCheckedEmails(new Set());
    } else {
      setCheckedEmails(new Set(selectableEmails.map((e: any) => e.email)));
    }
  };

  const handleSendEmail = async () => {
    if (checkedEmails.size === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await adminLeadsService.sendEmail(
        [lead.id],
        { [lead.id]: Array.from(checkedEmails) }
      );
      setSendResult({ sent: result.sent, failed: result.failed });

      const extras: string[] = [];
      if ((result.skippedSuppressed ?? 0) > 0) {
        extras.push(`${result.skippedSuppressed} suppressed`);
      }
      if ((result.skippedUndeliverable ?? 0) > 0) {
        extras.push(`${result.skippedUndeliverable} undeliverable skipped`);
      }
      if ((result.skippedCap ?? 0) > 0) {
        extras.push(`${result.skippedCap} daily cap skipped`);
      }
      if ((result.skippedDuplicate ?? 0) > 0) {
        extras.push(`${result.skippedDuplicate} duplicate merges`);
      }

      let message = '';
      if (result.sent > 0) {
        message = `Sent ${result.sent} email${result.sent !== 1 ? "s" : ""}`;
        if (extras.length > 0) message += `. ${extras.join(", ")}.`;
      } else {
        message = extras.length > 0 ? extras.join(", ") : "No messages sent";
      }
      if (result.failed > 0) {
        message += `${
          message ? " " : ""
        }(${result.failed} failed)`;
      }

      if (result.sent > 0) {
        toast.success(message.trim());
        fetchContactLogs();
        onLeadUpdated?.({ emailSentAt: new Date().toISOString() });
      } else {
        toast.error(message.trim() || "No messages sent");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to send emails");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-card shadow-2xl z-50 overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {lead.companyName || lead.domain}
            </h2>
            <a
              href={`https://${lead.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#d7785b] hover:underline flex items-center gap-1"
            >
              {lead.domain}
              <ExternalLink size={12} />
            </a>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-muted rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Section title="Basic Info">
            <Field label="Description" value={lead.description} />
            <Field label="Vertical" value={lead.vertical} />
            <Field label="Language" value={lead.language} />
            <Field label="Employees" value={lead.employeeCount} />
            <Field label="Platform" value={lead.platform} />
            <Field label="Theme" value={lead.theme} />
          </Section>

          <Section title="Location">
            <Field label="Country" value={lead.country} />
            <Field label="State" value={lead.state} />
            <Field label="City" value={lead.city} />
            <Field label="Address" value={lead.address} />
            <Field label="Zip" value={lead.zipCode} />
            <Field label="Timezone" value={lead.timezone} />
          </Section>

          {/* Contact Section - only shown when enriched with contact info */}
          {lead.enrichedAt && (emails.length > 0 || lead.phone) ? (
          <div>
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Contact</h3>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 rounded-full">
                <CheckCircle2 size={10} />
                Enriched {new Date(lead.enrichedAt).toLocaleDateString()}
              </span>
            </div>

            {lead.phone && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg mb-3">
                <Phone size={14} className="text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="text-xs font-medium text-muted-foreground">Phone</span>
                  <p className="text-sm font-medium text-foreground">{lead.phone}</p>
                </div>
              </div>
            )}

            {emails.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    Emails ({emails.length})
                    {selectableEmails.length !== emails.length && (
                      <span className="text-muted-foreground font-normal ml-1">
                        · {selectableEmails.length} sendable
                      </span>
                    )}
                  </span>
                  {canSendEmail && (
                    <button
                      onClick={toggleAll}
                      className="text-[10px] font-medium text-[#d7785b] hover:underline"
                    >
                      {selectableEmails.length > 0 &&
                      selectableEmails.every((x: any) =>
                        checkedEmails.has(x.email)
                      )
                        ? "Deselect all"
                        : "Select all"}
                    </button>
                  )}
                </div>
                {emails.map((e: any, i: number) => {
                  const blocked = isUndeliverableEntry(e);
                  return (
                  <div
                    key={i}
                    className={`flex items-start justify-between p-3 bg-muted rounded-lg ${
                      blocked ? "opacity-70" : ""
                    }`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      {canSendEmail || blocked ? (
                        <input
                          type="checkbox"
                          disabled={blocked}
                          checked={!blocked && checkedEmails.has(e.email)}
                          onChange={() => toggleEmail(e.email, blocked)}
                          className={`mt-0.5 h-4 w-4 rounded border-border text-[#d7785b] focus:ring-[#d7785b]/20 flex-shrink-0 ${
                            blocked
                              ? "opacity-40 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                          title={
                            blocked
                              ? "Undeliverable — not sent via broadcast"
                              : undefined
                          }
                        />
                      ) : (
                        <Mail
                          size={14}
                          className="text-muted-foreground mt-0.5 flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {e.email}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {e.email_type && (
                            <span className="text-[10px] text-muted-foreground">
                              {e.email_type}
                            </span>
                          )}
                          {e.validated && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-green-600 dark:text-green-400">
                              <Shield size={8} />
                              Verified
                            </span>
                          )}
                          {blocked && (
                            <span className="text-[10px] text-red-600 dark:text-red-400">
                              Undeliverable
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <DeliverabilityBadge score={e.email_deliverability} />
                  </div>
                );
              })}

                {canSendEmail && (
                  <div className="pt-2">
                    {sendResult ? (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted border border-border">
                        {sendResult.failed === 0 ? (
                          <CheckCircle size={16} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                        ) : (
                          <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        )}
                        <span className="text-sm text-muted-foreground">
                          {sendResult.sent} sent
                          {sendResult.failed > 0 &&
                            `, ${sendResult.failed} failed`}
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={handleSendEmail}
                        disabled={sending || checkedEmails.size === 0}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50"
                        style={{ backgroundColor: "#d7785b" }}
                      >
                        {sending ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={14} />
                            Send Email to {checkedEmails.size} Address
                            {checkedEmails.size !== 1 ? "es" : ""}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
          ) : null}

          <Section title="E-commerce">
            <Field label="Currency" value={lead.currency} />
            <Field label="Products" value={lead.productCount} />
            <Field label="Variants" value={lead.variantCount} />
            <Field
              label="Avg Price"
              value={
                lead.averageProductPrice
                  ? `$${lead.averageProductPrice.toFixed(2)}`
                  : null
              }
            />
          </Section>

          <Section title="Financial">
            <Field
              label="Est. Monthly Revenue"
              value={
                lead.estimatedMonthlyRevenue
                  ? `$${lead.estimatedMonthlyRevenue.toLocaleString()}`
                  : null
              }
            />
            <Field
              label="Tech Spend"
              value={
                lead.estimatedTechSpend
                  ? `$${lead.estimatedTechSpend.toLocaleString()}`
                  : null
              }
            />
            <Field
              label="App Spend"
              value={
                lead.estimatedAppSpend
                  ? `$${lead.estimatedAppSpend.toLocaleString()}`
                  : null
              }
            />
          </Section>

          <Section title="Metadata">
            <Field label="Source" value={lead.source} />
            <Field label="Lead Score" value={lead.leadScore} />
            <Field
              label="Created"
              value={new Date(lead.createdAt).toLocaleDateString()}
            />
          </Section>

          {/* Contact History - only shown when enriched with contact info */}
          {lead.enrichedAt && (emails.length > 0 || lead.phone) ? (
          <div>
            <div className="flex items-center justify-between mb-3 pb-1 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Contact History</h3>
              <button
                onClick={() => setShowPhoneForm((v) => !v)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: "#d7785b" }}
              >
                <PhoneCall size={12} />
                Log Phone Contact
              </button>
            </div>

            {showPhoneForm && (
              <div className="mb-3 p-3 bg-muted rounded-lg border border-border space-y-2">
                <textarea
                  value={phoneNote}
                  onChange={(e) => setPhoneNote(e.target.value)}
                  placeholder="Optional note about the call..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b] resize-none"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleLogPhoneContact}
                    disabled={loggingPhone}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "#d7785b" }}
                  >
                    {loggingPhone ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check size={12} />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { setShowPhoneForm(false); setPhoneNote(""); }}
                    className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-accent transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {logsLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : contactLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No contact history yet</p>
            ) : (
              <div className="space-y-2">
                {contactLogs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                    <div className="flex-shrink-0 mt-0.5">
                      {log.method === "email" ? (
                        <div className="w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                          <Mail size={14} className="text-blue-600 dark:text-blue-400" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center">
                          <PhoneCall size={14} className="text-green-600 dark:text-green-400" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                          log.method === "email"
                            ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300"
                            : "bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300"
                        }`}>
                          {log.method === "email" ? "Email" : "Phone"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          by {log.contactedBy.firstName} {log.contactedBy.lastName}
                        </span>
                      </div>
                      {log.note && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                          <MessageSquare size={10} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                          {log.note}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-1">
                        <Clock size={10} className="text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(log.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-2 pb-1 border-b border-border">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  if (value === null || value === undefined) return null;
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-muted-foreground">{String(value)}</p>
    </div>
  );
}

export function LeadsTable({
  leads,
  loading,
  onLoadMore,
  hasMore,
  loadingMore,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  footer,
}: LeadsTableProps) {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const selectable = !!onToggleSelect;

  const columns: ColumnDef<Lead>[] = [
    ...(selectable
      ? ([
          {
            id: "select",
            header: () => (
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-border text-[#d7785b] focus:ring-[#d7785b]/20 cursor-pointer"
              />
            ),
            cell: ({ row }) => (
              <input
                type="checkbox"
                checked={selectedIds?.has(row.original.id) ?? false}
                onChange={() => onToggleSelect?.(row.original.id)}
                className="h-4 w-4 rounded border-border text-[#d7785b] focus:ring-[#d7785b]/20 cursor-pointer"
              />
            ),
            meta: { width: "w-12" },
          },
        ] as ColumnDef<Lead>[])
      : []),
    {
      id: "domain",
      header: "Domain",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Globe size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-[#d7785b] truncate max-w-[200px]">
            {row.original.domain}
          </span>
        </div>
      ),
      meta: { width: "w-52" },
    },
    {
      id: "company",
      header: "Company",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground truncate max-w-[160px]">
            {row.original.companyName || "\u2014"}
          </span>
        </div>
      ),
      meta: { width: "w-44" },
    },
    {
      id: "country",
      header: "Country",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <MapPin size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">
            {row.original.country || "\u2014"}
          </span>
        </div>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "vertical",
      header: "Niches",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Tag size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground truncate max-w-[120px]">
            {row.original.vertical || "\u2014"}
          </span>
        </div>
      ),
      meta: { width: "w-36" },
    },
    {
      id: "products",
      header: "Products",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <ShoppingCart
            size={14}
            className="text-muted-foreground flex-shrink-0"
          />
          <span className="text-sm text-muted-foreground">
            {row.original.productCount ?? "\u2014"}
          </span>
        </div>
      ),
      meta: { width: "w-24" },
    },
    {
      id: "revenue",
      header: "Est. Revenue",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DollarSign size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">
            {row.original.estimatedMonthlyRevenue
              ? `$${row.original.estimatedMonthlyRevenue.toLocaleString()}`
              : "\u2014"}
          </span>
        </div>
      ),
      meta: { width: "w-32" },
    },
    {
      id: "contact",
      header: "Contact",
      cell: ({ row }) => <ContactCell lead={row.original} />,
      meta: { width: "w-52" },
    },
    {
      id: "emailSent",
      header: "Email Sent",
      cell: ({ row }) =>
        row.original.emailSentAt ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-50 dark:bg-green-500/10">
            <Check size={14} className="text-green-600 dark:text-green-400" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted">
            <Minus size={14} className="text-muted-foreground" />
          </span>
        ),
      meta: {
        width: "w-24",
        headerClassName: "text-center",
        cellClassName: "text-center",
      },
    },
    {
      id: "validation",
      header: "Validation",
      cell: ({ row }) => (
        <ValidationStatusBadge status={row.original.emailValidationStatus} />
      ),
      meta: {
        width: "w-28",
        headerClassName: "text-center",
        cellClassName: "text-center",
      },
    },
    {
      id: "phoneContacted",
      header: "Phone Contacted",
      cell: ({ row }) =>
        row.original.phoneContactedAt ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-50 dark:bg-green-500/10">
            <Check size={14} className="text-green-600 dark:text-green-400" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted">
            <Minus size={14} className="text-muted-foreground" />
          </span>
        ),
      meta: {
        width: "w-32",
        headerClassName: "text-center",
        cellClassName: "text-center",
      },
    },
    {
      id: "source",
      header: "Source",
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            row.original.source === "store_census"
              ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {row.original.source}
        </span>
      ),
      meta: { width: "w-32" },
    },
  ];

  return (
    <>
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={leads}
          isLoading={loading}
          skeletonRows={5}
          getRowId={(row) => row.id}
          onRowClick={(lead) => setSelectedLead(lead)}
          rowClassName={(lead) =>
            selectedIds?.has(lead.id) ? "bg-[#d7785b]/5" : undefined
          }
          emptyText={
            <span className="inline-block py-4">
              <Globe
                size={48}
                className="mx-auto text-muted-foreground/40 mb-4"
              />
              <span className="block text-lg font-medium text-muted-foreground mb-1">
                No leads found
              </span>
              <span className="block text-sm text-muted-foreground">
                Generate leads from StoreCensus or import from CSV.
              </span>
            </span>
          }
        />
        {footer}
      </div>

      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#d7785b" }}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onLeadUpdated={(updated) => {
            setSelectedLead((prev) => prev ? { ...prev, ...updated } : prev);
          }}
        />
      )}
    </>
  );
}
